import { Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

export const TooltipProvider = Provider;
export const TooltipTrigger = Trigger;

export type TooltipProps = ComponentPropsWithoutRef<typeof Root>;

/**
 * A tooltip that **carries its own provider**.
 *
 * Radix's `Root` throws outside a `Provider`, and the provider it wants is an
 * app-wide singleton — which is a trap the kit had been sitting on since P9: the
 * component shipped, nothing mounted a provider, and the first consumer would
 * have discovered it not at the call site but in every spec that renders an
 * ancestor of it. Providing here makes a tooltip a component rather than a
 * component plus a precondition.
 *
 * What that costs is `skipDelayDuration` — the Radix behaviour where, once one
 * tooltip is open, its neighbours open instantly. That is shared state and it
 * needs a shared provider. A screen that genuinely wants it (a toolbar of icon
 * buttons) wraps its own `TooltipProvider`, still exported above: nesting is
 * legal and the nearest one wins.
 */
export function Tooltip({ delayDuration = 300, ...props }: TooltipProps) {
  return (
    <Provider delayDuration={delayDuration}>
      <Root delayDuration={delayDuration} {...props} />
    </Provider>
  );
}

export type TooltipContentProps = ComponentPropsWithoutRef<typeof Content>;

/**
 * A tooltip is a HINT, never the only place information lives: it is
 * unreachable on touch and it disappears the moment focus moves. Every
 * icon-only control in this kit therefore carries its own `aria-label`, and the
 * tooltip repeats that label for sighted mouse users.
 *
 * `side="top"` with `sideOffset` rather than a physical offset, so nothing here
 * needs to know which way the document runs.
 */
export const TooltipContent = forwardRef<ElementRef<typeof Content>, TooltipContentProps>(
  function TooltipContent({ className, sideOffset = 6, ...props }, ref) {
    return (
      <Portal>
        <Content
          ref={ref}
          sideOffset={sideOffset}
          className={cn(
            'z-tooltip overflow-hidden rounded-md bg-foreground px-md py-1.5 text-caption text-background shadow-md',
            'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            className,
          )}
          {...props}
        />
      </Portal>
    );
  },
);
